import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  CheckCircle, Car, Upload, X, User, Phone, Mail, FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const vitrageTypes = [
  "Pare-brise avant",
  "Lunette arrière",
  "Vitre latérale avant gauche",
  "Vitre latérale avant droite",
  "Vitre latérale arrière gauche",
  "Vitre latérale arrière droite",
  "Toit panoramique",
  "Autre",
];

export default function QuotePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    immatriculation: "",
    marque: "",
    modele: "",
    type_vitrage: "",
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setPhotos([...photos, ...newPhotos].slice(0, 5)); // Max 5 photos
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type_vitrage) {
      toast.error("Veuillez sélectionner le type de vitrage");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });
      photos.forEach((photo) => {
        submitData.append("photos", photo.file);
      });

      const response = await fetch(`${API}/quotes`, {
        method: "POST",
        body: submitData,
      });

      if (!response.ok) throw new Error("Erreur lors de l'envoi du devis");

      setIsSuccess(true);
      toast.success("Votre demande de devis a été envoyée !");
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="pt-20 sm:pt-24 lg:pt-28 min-h-screen flex items-center justify-center" data-testid="quote-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-12 rounded-3xl text-center max-w-lg mx-4"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Demande envoyée !
          </h2>
          <p className="text-slate-300 mb-8">
            Nous avons bien reçu votre demande de devis. 
            Notre équipe vous contactera dans les plus brefs délais.
          </p>
          <Button
            onClick={() => window.location.href = "/"}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8"
          >
            Retour à l'accueil
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-24 lg:pt-28" data-testid="quote-page">
      {/* Hero */}
      <section className="section-padding bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              Devis gratuit
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Demande de devis
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Remplissez le formulaire ci-dessous et recevez votre devis personnalisé sous 24h.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit}
            className="glass-card p-8 md:p-12 rounded-3xl"
          >
            {/* Vehicle Info */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <Car className="w-6 h-6 text-sky-400" />
                <h3 className="text-xl font-semibold text-white">
                  Informations véhicule
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="immatriculation" className="mb-2 block">
                    Immatriculation
                  </Label>
                  <Input
                    id="immatriculation"
                    value={formData.immatriculation}
                    onChange={(e) => setFormData({ ...formData, immatriculation: e.target.value.toUpperCase() })}
                    placeholder="AB-123-CD"
                    required
                    className="bg-slate-800/50 uppercase"
                    data-testid="input-immatriculation"
                  />
                </div>

                <div>
                  <Label htmlFor="marque" className="mb-2 block">
                    Marque
                  </Label>
                  <Input
                    id="marque"
                    value={formData.marque}
                    onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                    placeholder="Ex: Renault, Peugeot..."
                    required
                    className="bg-slate-800/50"
                    data-testid="input-marque"
                  />
                </div>

                <div>
                  <Label htmlFor="modele" className="mb-2 block">
                    Modèle
                  </Label>
                  <Input
                    id="modele"
                    value={formData.modele}
                    onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                    placeholder="Ex: Clio, 308..."
                    required
                    className="bg-slate-800/50"
                    data-testid="input-modele"
                  />
                </div>

                <div>
                  <Label htmlFor="type_vitrage" className="mb-2 block">
                    Type de vitrage
                  </Label>
                  <Select 
                    value={formData.type_vitrage} 
                    onValueChange={(value) => setFormData({ ...formData, type_vitrage: value })}
                  >
                    <SelectTrigger className="bg-slate-800/50" data-testid="select-vitrage">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vitrageTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Photo Upload */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Upload className="w-5 h-5 text-sky-400" />
                <h3 className="text-lg font-semibold text-white">
                  Photos du dégât (optionnel)
                </h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Ajoutez jusqu'à 5 photos pour nous aider à évaluer les dégâts.
              </p>

              <div className="flex flex-wrap gap-4">
                {photos.map((photo, index) => (
                  <div key={index} className="relative w-24 h-24 rounded-xl overflow-hidden">
                    <img
                      src={photo.preview}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-sky-500 hover:text-sky-400 transition-colors"
                    data-testid="upload-photo-btn"
                  >
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-xs">Ajouter</span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>

            {/* Contact Info */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-6 h-6 text-sky-400" />
                <h3 className="text-xl font-semibold text-white">
                  Vos coordonnées
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-sky-400" />
                    Nom complet
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-sky-400" />
                    Téléphone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="06 00 00 00 00"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-phone"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-sky-400" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jean.dupont@email.com"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-email"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="message" className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-sky-400" />
                    Message (optionnel)
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Informations complémentaires..."
                    className="bg-slate-800/50 min-h-[100px]"
                    data-testid="input-message"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 text-lg font-semibold"
              data-testid="submit-quote"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2 justify-center">
                  <div className="spinner" />
                  Envoi en cours...
                </span>
              ) : (
                "Envoyer ma demande de devis"
              )}
            </Button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
